import { FUNDING_PURPOSES, FUNDS_STATUSES, GAP_TYPES, MATCH_STATUSES, OFFICE_ACTIONS, PAYMENT_RAILS, REVIEW_STATES } from "../domain/constants";

export type FieldDef = {
  path: string;
  label: string;
  type?: "text" | "number" | "date" | "datetime-local" | "textarea" | "select" | "checkbox";
  options?: readonly string[];
  wide?: boolean;
};

export type StepDef = {
  id: string;
  title: string;
  help: string;
  fields: FieldDef[];
};

export const REVIEW_STEPS: StepDef[] = [
  {
    id: "start",
    title: "Start Buyer Funding Review",
    help: "Create the control record before buyer funds are requested, accepted, marked ready, applied, or relied on.",
    fields: [
      { path: "fileNumber", label: "File / order number" },
      { path: "officeName", label: "Office" },
      { path: "createdBy", label: "Reviewer" },
      { path: "buyerParties.0.name", label: "Buyer name" },
      { path: "buyerParties.0.role", label: "Buyer role", type: "select", options: ["buyer", "co_buyer", "entity_buyer", "trustee", "attorney_in_fact", "third_party_funder", "unknown"] },
      { path: "propertyAddress", label: "Property address", type: "textarea", wide: true },
      { path: "closingDate", label: "Closing date", type: "date" },
      { path: "fundingPurpose", label: "Funding purpose", type: "select", options: FUNDING_PURPOSES },
      { path: "expectedAmount.amountCents", label: "Expected amount cents", type: "number" },
      { path: "dueAt", label: "Due date / cutoff", type: "datetime-local" },
      { path: "reviewReason", label: "Review reason" },
      { path: "tpsFileLink", label: "Optional TPS file link" }
    ]
  },
  {
    id: "expected-source",
    title: "What amount was expected?",
    help: "Record the source document, extracted fields, confidence, hash, and any retained source gaps.",
    fields: [
      { path: "expectedFunds.sourceDocumentId", label: "Source document" },
      { path: "expectedFunds.sourceStatus", label: "Source status", type: "select", options: ["preliminary", "revised", "final", "unknown"] },
      { path: "expectedFunds.preparedBy", label: "Prepared by" },
      { path: "expectedFunds.approvedBy", label: "Reviewed by, if shown" },
      { path: "expectedFunds.extractionConfidence", label: "Extraction confidence", type: "number" },
      { path: "fundingInstruction.hash", label: "Source hash" }
    ]
  },
  {
    id: "expected-funds",
    title: "Confirm expected funds",
    help: "Confirm amount, purpose, due date, source status, prior amount, and reason for any change.",
    fields: [
      { path: "expectedFunds.amount.amountCents", label: "Expected amount cents", type: "number" },
      { path: "expectedFunds.purpose", label: "Funding purpose", type: "select", options: FUNDING_PURPOSES },
      { path: "expectedFunds.dueAt", label: "Due date / cutoff", type: "datetime-local" },
      { path: "expectedFunds.priorAmount.amountCents", label: "Prior expected amount cents", type: "number" },
      { path: "expectedFunds.differenceFromPrior.amountCents", label: "Difference from prior cents", type: "number" }
    ]
  },
  {
    id: "instructions",
    title: "What instructions did the office send or rely on?",
    help: "Record active instruction version, sent details, channel, account summary, rail, memo, and superseded instruction.",
    fields: [
      { path: "fundingInstruction.sourceType", label: "Instruction source type" },
      { path: "fundingInstruction.versionLabel", label: "Instruction version" },
      { path: "fundingInstruction.sentBy", label: "Sent by" },
      { path: "fundingInstruction.sentTo", label: "Sent to" },
      { path: "fundingInstruction.sentAt", label: "Sent timestamp", type: "datetime-local" },
      { path: "fundingInstruction.deliveryChannel", label: "Delivery channel" },
      { path: "fundingInstruction.receivingBankName", label: "Receiving bank" },
      { path: "fundingInstruction.receivingAccountLast4", label: "Receiving account last four" },
      { path: "fundingInstruction.routingLast4", label: "Routing last four" },
      { path: "fundingInstruction.beneficiaryName", label: "Beneficiary / escrow account name" },
      { path: "fundingInstruction.allowedRails.0", label: "Payment rail allowed", type: "select", options: PAYMENT_RAILS },
      { path: "fundingInstruction.deadlineAt", label: "Payment deadline", type: "datetime-local" },
      { path: "fundingInstruction.memoRequired", label: "File number / memo required" },
      { path: "fundingInstruction.priorVersionLabel", label: "Prior instruction version" },
      { path: "buyerFundingFlow.knownEntryPoint", label: "Known Veto entry point" },
      { path: "buyerFundingFlow.officePublishedAt", label: "Office published timestamp", type: "datetime-local" },
      { path: "fundingInstruction.changeReason", label: "Change reason", type: "textarea", wide: true }
    ]
  },
  {
    id: "acknowledgment",
    title: "Did the buyer acknowledge the instructions?",
    help: "Record acknowledgment without implying an office decision or final funds status.",
    fields: [
      { path: "buyerAcknowledgment.type", label: "Acknowledgment type" },
      { path: "buyerAcknowledgment.partyName", label: "Buyer / party" },
      { path: "buyerAcknowledgment.acknowledgedAmount.amountCents", label: "Acknowledged amount cents", type: "number" },
      { path: "buyerAcknowledgment.acknowledgedInstructionVersion", label: "Acknowledged instruction version" },
      { path: "buyerAcknowledgment.acknowledgedRail", label: "Acknowledged payment method", type: "select", options: PAYMENT_RAILS },
      { path: "buyerAcknowledgment.acknowledgedDeadlineAt", label: "Acknowledged deadline", type: "datetime-local" },
      { path: "buyerAcknowledgment.acknowledgedAt", label: "Timestamp", type: "datetime-local" },
      { path: "buyerFundingFlow.buyerViewedAt", label: "Buyer viewed timestamp", type: "datetime-local" },
      { path: "buyerFundingFlow.acknowledgmentStatement", label: "Acknowledgment statement", type: "textarea", wide: true },
      { path: "buyerAcknowledgment.channel", label: "Channel" },
      { path: "buyerAcknowledgment.sourceDocumentId", label: "Source file" },
      { path: "buyerAcknowledgment.limitation", label: "Operator note / limitation", type: "textarea", wide: true }
    ]
  },
  {
    id: "funding-source",
    title: "Who is funding the payment?",
    help: "Record funding party, source type, policy status, gift/entity/trust evidence, and retained limitations.",
    fields: [
      { path: "fundingSource.partyType", label: "Party type" },
      { path: "fundingSource.fundingPartyName", label: "Funding party name" },
      { path: "fundingSource.relationshipToBuyer", label: "Relationship to buyer" },
      { path: "fundingSource.sourceType", label: "Expected source type", type: "select", options: ["buyer_personal", "joint", "entity", "trust", "gift", "third_party", "lender", "unknown"] },
      { path: "fundingSource.approvedUnderPolicy", label: "Source allowed by office policy?", type: "checkbox" },
      { path: "fundingSource.giftEvidenceAttached", label: "Gift-funds evidence attached?", type: "checkbox" },
      { path: "fundingSource.authorityEvidenceAttached", label: "Entity / trust authority evidence attached?", type: "checkbox" },
      { path: "fundingSource.policyException", label: "Office policy exception?", type: "checkbox" },
      { path: "fundingSource.limitation", label: "Source policy note", type: "textarea", wide: true }
    ]
  },
  {
    id: "source-evidence",
    title: "What source account evidence was recorded?",
    help: "Record provider or manual evidence as a source row with limitation. Provider evidence is not final authority.",
    fields: [
      { path: "sourceAccountEvidence.evidenceType", label: "Evidence type" },
      { path: "sourceAccountEvidence.provider", label: "Provider", type: "select", options: ["plaid", "certifid", "closinglock", "qualia", "bank_statement", "bank_letter", "voided_check", "wire_receipt", "manual", "other"] },
      { path: "sourceAccountEvidence.recordedAt", label: "Evidence timestamp", type: "datetime-local" },
      { path: "sourceAccountEvidence.ownerName", label: "Account owner name" },
      { path: "sourceAccountEvidence.ownerMatchResult", label: "Account owner match result", type: "select", options: MATCH_STATUSES },
      { path: "sourceAccountEvidence.bankName", label: "Bank name" },
      { path: "sourceAccountEvidence.accountLast4", label: "Account last four" },
      { path: "sourceAccountEvidence.accountType", label: "Account type" },
      { path: "sourceAccountEvidence.balanceRecorded", label: "Balance evidence recorded?", type: "checkbox" },
      { path: "sourceAccountEvidence.balanceAmount.amountCents", label: "Balance amount cents", type: "number" },
      { path: "sourceAccountEvidence.balanceAsOf", label: "Available balance timestamp", type: "datetime-local" },
      { path: "sourceAccountEvidence.sourceParty", label: "Source party" },
      { path: "sourceAccountEvidence.staleAfter", label: "Expiration / stale-after date", type: "date" },
      { path: "sourceAccountEvidence.limitation", label: "Limitation", type: "textarea", wide: true }
    ]
  },
  {
    id: "rail",
    title: "How were funds supposed to move?",
    help: "Record rail, instruction policy fit, finality, return risk, provider, fees, and cutoffs.",
    fields: [
      { path: "paymentRail.rail", label: "Rail selected", type: "select", options: PAYMENT_RAILS },
      { path: "paymentRail.allowedByInstructions", label: "Rail allowed by instructions?", type: "select", options: ["yes", "no", "unknown"] },
      { path: "paymentRail.allowedByPolicy", label: "Rail allowed by office policy?", type: "select", options: ["yes", "no", "unknown"] },
      { path: "paymentRail.expectedArrivalTiming", label: "Expected arrival timing" },
      { path: "paymentRail.returnRiskNote", label: "Finality / return-risk note", type: "textarea", wide: true },
      { path: "paymentRail.processorProvider", label: "Processor / provider" },
      { path: "paymentRail.feeNote", label: "Fee, if relevant" },
      { path: "paymentRail.buyerFacingDeadline", label: "Buyer-facing deadline", type: "datetime-local" },
      { path: "paymentRail.officeCutoff", label: "Office cutoff", type: "datetime-local" }
    ]
  },
  {
    id: "initiation",
    title: "What payment initiation evidence exists?",
    help: "Record amount, sender, destination, trace/reference, memo, source document, and limitation.",
    fields: [
      { path: "paymentInitiation.evidenceType", label: "Evidence type" },
      { path: "paymentInitiation.initiatedAmount.amountCents", label: "Initiated amount cents", type: "number" },
      { path: "paymentInitiation.initiatedBy", label: "Initiated by" },
      { path: "paymentInitiation.initiatedAt", label: "Initiation timestamp", type: "datetime-local" },
      { path: "paymentInitiation.originatingBank", label: "Originating bank" },
      { path: "paymentInitiation.senderShown", label: "Sender shown" },
      { path: "paymentInitiation.destinationShown", label: "Destination shown" },
      { path: "paymentInitiation.traceNumber", label: "Trace / reference" },
      { path: "paymentInitiation.fileMemo", label: "File number / memo" },
      { path: "paymentInitiation.rail", label: "Payment rail", type: "select", options: PAYMENT_RAILS },
      { path: "paymentInitiation.sourceDocumentId", label: "Source document" },
      { path: "paymentInitiation.limitation", label: "Limitation", type: "textarea", wide: true }
    ]
  },
  {
    id: "inbound",
    title: "What arrived?",
    help: "Record inbound receipt, ledger, return/reversal status, application state, and accounting reviewer.",
    fields: [
      { path: "inboundReceipt.sourceType", label: "Receipt source", type: "select", options: ["bank_portal", "trust_ledger", "tps", "rynoh", "payment_provider", "ach_report", "wire_report", "cashiers_check_deposit", "manual", "not_received", "unable_to_check"] },
      { path: "inboundReceipt.amountReceived.amountCents", label: "Amount received cents", type: "number" },
      { path: "inboundReceipt.receivedAt", label: "Received timestamp", type: "datetime-local" },
      { path: "inboundReceipt.receivingAccountLast4", label: "Receiving account last four" },
      { path: "inboundReceipt.senderShown", label: "Sender shown" },
      { path: "inboundReceipt.originatingBank", label: "Originating bank" },
      { path: "inboundReceipt.rail", label: "Payment rail", type: "select", options: PAYMENT_RAILS },
      { path: "inboundReceipt.reference", label: "Reference / memo" },
      { path: "inboundReceipt.traceNumber", label: "Trace number" },
      { path: "inboundReceipt.fileNumber", label: "File number" },
      { path: "inboundReceipt.trustLedgerEntryId", label: "Trust ledger entry" },
      { path: "inboundReceipt.depositStatus", label: "Deposit status" },
      { path: "inboundReceipt.returnRiskStatus", label: "Return / reversal status", type: "select", options: ["none_recorded", "open", "returned", "reversed", "unknown"] },
      { path: "inboundReceipt.appliedToFile", label: "Applied to file?", type: "checkbox" },
      { path: "inboundReceipt.accountingReviewer", label: "Accounting reviewer" }
    ]
  },
  {
    id: "match",
    title: "Review funding match",
    help: "Review generated field-level match rows. There is no aggregate risk score.",
    fields: [
      { path: "status", label: "Review status", type: "select", options: REVIEW_STATES },
      { path: "fundsStatus", label: "Funds status", type: "select", options: FUNDS_STATUSES }
    ]
  },
  {
    id: "gaps",
    title: "What stayed open?",
    help: "Gaps must appear on the receipt, map to source rows, and remain retained unless a later version resolves them.",
    fields: [
      { path: "openGaps.0.gapType", label: "Gap type", type: "select", options: GAP_TYPES },
      { path: "openGaps.0.affectedPartyName", label: "Affected party" },
      { path: "openGaps.0.affectedAmount.amountCents", label: "Affected amount cents", type: "number" },
      { path: "openGaps.0.description", label: "Why it matters", type: "textarea", wide: true },
      { path: "openGaps.0.officeActionRequired", label: "Office action required?", type: "checkbox" },
      { path: "openGaps.0.exceptionRequired", label: "Exception required?", type: "checkbox" },
      { path: "openGaps.0.reviewerNote", label: "Reviewer note", type: "textarea", wide: true },
      { path: "openGaps.0.managerNote", label: "Manager note", type: "textarea", wide: true },
      { path: "openGaps.0.status", label: "Status", type: "select", options: ["open", "acknowledged", "resolved", "retained"] }
    ]
  },
  {
    id: "office-action",
    title: "What did the office do?",
    help: "Record the office action after the four-way funding match and gaps are reviewed.",
    fields: [
      { path: "officeAction.action", label: "Office action", type: "select", options: OFFICE_ACTIONS },
      { path: "officeAction.fundsStatus", label: "Funds status", type: "select", options: FUNDS_STATUSES },
      { path: "officeAction.appliedAmount.amountCents", label: "Applied amount cents", type: "number" },
      { path: "officeAction.ledgerReference", label: "Ledger reference" },
      { path: "officeAction.returnReason", label: "Return reason", type: "textarea", wide: true },
      { path: "officeAction.returnMethod", label: "Return method" },
      { path: "officeAction.returnReference", label: "Return reference" },
      { path: "officeAction.exceptionReason", label: "Exception reason", type: "textarea", wide: true },
      { path: "officeAction.managerReviewStatus", label: "Manager / owner review", type: "select", options: ["not_required", "requested", "completed", "owner_exception_recorded"] },
      { path: "officeAction.note", label: "Office action note", type: "textarea", wide: true }
    ]
  },
  {
    id: "signoff",
    title: "Reviewer sign-off",
    help: "Sign the receipt boundary statement and record funds status, gaps acknowledgment, and office action.",
    fields: [
      { path: "signoff.signerName", label: "Reviewer" },
      { path: "signoff.role", label: "Role" },
      { path: "signoff.office", label: "Office" },
      { path: "signoff.authenticationMethod", label: "Authentication method" },
      { path: "signoff.signedAt", label: "Signed at", type: "datetime-local" },
      { path: "signoff.fundsStatusAtSignoff", label: "Funds status at sign-off", type: "select", options: FUNDS_STATUSES },
      { path: "signoff.gapsAcknowledged", label: "Gaps acknowledged", type: "checkbox" },
      { path: "signoff.officeAction", label: "Office action", type: "select", options: OFFICE_ACTIONS },
      { path: "signoff.managerSignoff", label: "Optional manager sign-off" },
      { path: "signoff.ownerExceptionApproval", label: "Optional owner exception approval" }
    ]
  },
  {
    id: "check",
    title: "Check answers before export",
    help: "Review all receipt sections, source rows, gaps, office action, and sign-off before export.",
    fields: [{ path: "saveBackLocation", label: "Save-back location" }]
  },
  {
    id: "export",
    title: "Export Buyer Funding Review Receipt",
    help: "Generate receipt, appendix, source manifest, event log, packet JSON, hashes, and save-back note.",
    fields: [
      { path: "receiptVersion", label: "Receipt version" },
      { path: "supersedesReviewId", label: "Supersedes review ID" }
    ]
  }
];
